/**
 * The public surface of the confidence display.
 *
 * `SegmentedMeter`, `ConfidenceCardLayout`, `ConfidenceGuidance` and the ladder/statement/guidance
 * tables are **deliberately absent**. They are the primitive tier, and the components below are the
 * only sanctioned way to turn a confidence level into a rendering. A call site importing a
 * primitive directly would be re-deriving the level-to-ladder, level-to-statement or
 * level-to-guidance mapping, which is precisely what this tier exists to keep in one place.
 */
export { default as DocumentConfidenceMeter } from './DocumentConfidenceMeter';
export type { TDocumentConfidenceMeterProps } from './DocumentConfidenceMeter';

export { default as BiometricConfidenceMeter } from './BiometricConfidenceMeter';
export type { TBiometricConfidenceMeterProps } from './BiometricConfidenceMeter';

export { default as DocumentConfidenceLabel } from './DocumentConfidenceLabel';
export type { TDocumentConfidenceLabelProps } from './DocumentConfidenceLabel';

export { default as BiometricConfidenceLabel } from './BiometricConfidenceLabel';
export type { TBiometricConfidenceLabelProps } from './BiometricConfidenceLabel';

export { default as ConfidenceAccordion } from './ConfidenceAccordion';
export type { TConfidenceAccordionProps } from './ConfidenceAccordion';

export { default as DocumentConfidenceCard } from './DocumentConfidenceCard';
export type { TDocumentConfidenceCardProps } from './DocumentConfidenceCard';

export { default as BiometricConfidenceCard } from './BiometricConfidenceCard';
export type { TBiometricConfidenceCardProps } from './BiometricConfidenceCard';

/**
 * The card treatment is exported as a component, not as its words: a view handed only the words
 * would also have to choose the severity, the variant and the live-region role, and those are
 * presentation decisions this package owns. The view decides only *whether* the card is in the
 * state that needs it.
 *
 * **The words are exported too, because a second surface says the same thing.** When the control is
 * pressed anyway — the caller reached it under an unresolved lookup, or the permission changed
 * after the page loaded — the notification has to give the same account as the card would have. Two
 * copies of one sentence is how a wording change lands in one place and not the other, which is the
 * defect `requestCopy.ts` and `statements.ts` exist to prevent.
 */
export { default as PermissionNotice } from './PermissionNotice';
export {
  PERMISSION_NOTICE_BODY,
  PERMISSION_NOTICE_TITLE,
  REQUEST_UNAVAILABLE_BODY,
  REQUEST_UNAVAILABLE_TITLE,
} from './PermissionNotice';
export type { TPermissionNoticeProps } from './PermissionNotice';

export { default as EvidenceThumbnail } from './EvidenceThumbnail';
export type { TEvidenceThumbnailProps } from './EvidenceThumbnail';

export { default as CaptureViewer } from './CaptureViewer';
export type { TCapture, TCaptureEntry, TCaptureViewerProps } from './CaptureViewer';

export { default as BiometricEvidenceDialog } from './BiometricEvidenceDialog';
export type { TBiometricEvidenceDialogProps } from './BiometricEvidenceDialog';

export { default as EvidenceFindings, OutcomeReason } from './EvidenceFindings';
export type { TEvidenceFindingsProps, TOutcomeReasonProps } from './EvidenceFindings';

export { default as EvidenceProvenance } from './EvidenceProvenance';
export type { TEvidenceProvenanceProps } from './EvidenceProvenance';

export type {
  TBiometricConfidenceLevel,
  TDocumentConfidenceLevel,
  TLivenessDetermination,
} from './types';
